import { KeyboardEvent, useEffect, useRef, useState } from 'react';

type UseForecastPaginatedTableEditableCellProps = {
  value: number;
  sku: string;
  monthId: string;
  onUpdate: (sku: string, monthId: string, value: number) => void;
  isHistory: boolean;
  editable: boolean;
};

export function useForecastPaginatedTableEditableCell({
  value,
  sku,
  monthId,
  onUpdate,
  isHistory,
  editable,
}: UseForecastPaginatedTableEditableCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(`${value}`);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = () => {
    if (!isHistory && editable && cellRef.current) {
      const rect = cellRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
      });
      setIsEditing(true);
      setEditValue(value.toString());
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    const newValue = parseFloat(editValue);
    if (!isNaN(newValue) && newValue >= 0 && newValue !== value) {
      onUpdate(sku, monthId, newValue);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(value.toString());
    }
  };

  return {
    isEditing,
    editValue,
    position,
    inputRef,
    cellRef,
    handleDoubleClick,
    handleBlur,
    handleKeyDown,
    setEditValue,
  };
}
